import { t } from '@lingui/core/macro';
import { NoddiDialog } from 'noddi-ui-web';
import { useEffect } from 'react';
import { useUserGroupSelection } from '../../hooks/useUserGroupSelection';
import { useFlowUserGroupId } from '../../stores/flowUserGroupStore';
import {
  useKeepBookingDrafts,
  useShowUserGroupDialog,
  useUserGroupDialogActions
} from '../../stores/userGroupDialogStore';
import { resolveAutoSelectedUserGroup } from '../../utils/userGroupContext';
import { UserGroupSelection } from './UserGroupSelection';

export const UserGroupSelectionDialog = () => {
  const { selectUserGroup, userData, selectableUserGroups } = useUserGroupSelection();
  const showUserGroupDialog = useShowUserGroupDialog();
  const keepBookingDrafts = useKeepBookingDrafts();
  const flowUserGroupId = useFlowUserGroupId();
  const { closeUserGroupDialog } = useUserGroupDialogActions();

  // A flow that owns its group (a booking proposal) never offers a choice: a member
  // lands in that group, a non-member is stopped by the flow's access gate.
  const flowOwnsUserGroup = flowUserGroupId != null;
  const hasSession = !!userData;
  // Never pick on the customer's behalf when there is a real choice.
  const autoSelectGroup = resolveAutoSelectedUserGroup(selectableUserGroups, flowUserGroupId);

  useEffect(() => {
    // Memberships arrive with the session; deciding before that would close a dialog
    // restored from session storage, or skip the member's own group.
    if (!showUserGroupDialog || !hasSession) {
      return;
    }
    if (autoSelectGroup) {
      selectUserGroup(autoSelectGroup.id, { keepBookingDrafts });
      closeUserGroupDialog();
      return;
    }
    // Nothing left to ask — leaving the flag set would keep an invisible dialog "open"
    // for the rest of the session.
    if (flowOwnsUserGroup || selectableUserGroups.length < 2) {
      closeUserGroupDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy: reason not recorded; auto-selects a group or closes the dialog once the session has loaded
  }, [showUserGroupDialog, hasSession, autoSelectGroup, flowOwnsUserGroup, selectableUserGroups.length]);

  if (!userData || autoSelectGroup || flowOwnsUserGroup || selectableUserGroups.length < 2) {
    return null;
  }

  const handleUserGroupSelect = (userGroup: { id: number; name: string }) => {
    selectUserGroup(userGroup.id, { keepBookingDrafts });
    closeUserGroupDialog();
  };

  return (
    <NoddiDialog
      open={showUserGroupDialog}
      // users should not be able to close this dialog without selecting a user group
      onClose={() => {}}
      showCloseButton={false}
      title={t`Select user`}
      description={t`Your account is linked to multiple users. Please choose the user you'd like to make a booking for. To make bookings for a company car, choose the organization.`}
    >
      <UserGroupSelection onSelect={handleUserGroupSelect} userGroups={selectableUserGroups} />
    </NoddiDialog>
  );
};
